import CreatableSelect from "react-select/creatable";
import React, { useState } from "react";
import { isImmutable } from "immutable";
import { useTranslation } from "react-i18next";

type DomainsFieldProps = {
  handleUpdateField: (field: string, value: string) => void;
  domains: string;
  isGrid?: boolean;
  variant?: "default" | "inline";
};

const DomainsField = ({
  handleUpdateField,
  domains,
  isGrid = false,
  variant = "default",
}: DomainsFieldProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const formatDomain = (domain: string) => {
    if (!domain.trim()) return domain;

    let cleanDomain = domain.replace(/^https?:\/\//, "");
    cleanDomain = cleanDomain.replace(/^www\./, "");
    cleanDomain = cleanDomain.replace(/\/$/, "");
    return cleanDomain.trim().toLowerCase();
  };

  const validateDomain = (domain: string) => {
    if (!domain.trim()) return false;

    const domainRegex =
      /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return domainRegex.test(domain);
  };

  const getDomainOptions = () => {
    if (!domains) return [];
    if (isImmutable(domains)) {
      return domains.toJS().map((domain: any) => ({
        value: domain,
        label: domain,
      }));
    }
    if (typeof domains === "string") {
      return domains
        .split(",")
        .filter(Boolean)
        .map((domain) => {
          const formattedDomain = formatDomain(domain);
          return {
            value: formattedDomain,
            label: formattedDomain,
          };
        });
    }
    return [];
  };

  const domainOptions = getDomainOptions();

  const isInline = variant === "inline";
  const normalStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: isInline ? 28 : 36,
      height: isInline ? 28 : undefined,
      width: "100%",
      borderColor: state.isFocused ? "#93c5fd" : "#d1d5db", // blue-300 on focus, gray-300 default
      boxShadow: "none",
      ":hover": {
        borderColor: state.isFocused ? "#93c5fd" : "#d1d5db",
      },
      fontSize: isInline ? 12 : base.fontSize,
      lineHeight: isInline ? "16px" : base.lineHeight,
    }),
    valueContainer: (base) => ({
      ...base,
      padding: isInline ? "0 6px" : base.padding,
      alignItems: "center",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: isInline ? 28 : base.height,
      alignItems: "center",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: isInline ? 4 : base.padding,
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: isInline ? 4 : base.padding,
    }),
    inputContainer: (base) => ({
      ...base,
      margin: isInline ? "0" : base.margin,
      padding: isInline ? "0" : base.padding,
      alignItems: "center",
      display: "flex",
    }),
    input: (base) => ({
      ...base,
      fontSize: isInline ? "12px" : base.fontSize,
      lineHeight: isInline ? "16px" : base.lineHeight,
      fontWeight: isInline ? "500" : base.fontWeight,
      color: isInline ? "rgb(31 41 55 / 1)" : base.color, // gray-800
      margin: isInline ? "0" : base.margin,
      padding: isInline ? "0" : base.padding,
    }),
    indicatorSeparator: (base) => ({
      ...base,
      display: "none",
    }),
    placeholder: (base) => ({
      ...base,
      color: isInline ? "rgb(156 163 175 / 1)" : "rgb(156 163 175 / 1)", // gray-400
      fontSize: isInline ? "12px" : "14px",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "rgb(243 244 246 / 1)",
      borderRadius: "4px",
      margin: isInline ? "0 4px 0 0" : base.margin,
      alignItems: "center",
    }),
    multiValueContainer: (base) => ({
      ...base,
      alignItems: "center",
      display: "flex",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "rgb(55 65 81 / 1)",
      fontSize: isInline ? "12px" : "14px",
      lineHeight: isInline ? "16px" : base.lineHeight,
      fontWeight: isInline ? "500" : base.fontWeight,
      padding: isInline ? "2px 4px" : base.padding,
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "rgb(107 114 128 / 1)",
      "&:hover": {
        backgroundColor: "rgb(239 68 68 / 1)",
        color: "white",
      },
    }),
  };

  const handleChange = (values: any) => {
    if (values && values.length > 0) {
      const processedDomains = values.map((value: any) => {
        const formattedDomain = formatDomain(value.value);
        return formattedDomain;
      });

      const validDomains = processedDomains.filter(validateDomain);
      handleUpdateField("domains", validDomains.join(","));
    } else {
      handleUpdateField("domains", "");
    }
  };

  const handleCreate = (inputValue: string) => {
    const formattedDomain = formatDomain(inputValue);
    if (validateDomain(formattedDomain)) {
      const newOptions = [...domainOptions, { value: formattedDomain, label: formattedDomain }];
      const domainValues = newOptions.map((option) => option.value);
      handleUpdateField("domains", domainValues.join(","));
    }
  };

  const renderGridView = () => {
    if (isEditing) {
      return (
        <CreatableSelect
          placeholder={t("Add domains (e.g., example.com)")}
          isMulti
          styles={normalStyles}
          isClearable={false}
          value={domainOptions}
          options={domainOptions}
          onChange={handleChange}
          onCreateOption={handleCreate}
          onBlur={() => setIsEditing(false)}
          autoFocus
          formatCreateLabel={(inputValue) =>
            t('Add "{{domain}}"', { domain: formatDomain(inputValue) })
          }
          isValidNewOption={(inputValue) => {
            const formatted = formatDomain(inputValue);
            return formatted.length > 0 && validateDomain(formatted);
          }}
        />
      );
    }

    if (domainOptions.length === 0) {
      return (
        <div
          className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 transition-colors"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsEditing(true);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsEditing(true);
            }
          }}
        >
          {t("No domains")}
        </div>
      );
    }

    return (
      <div
        className="flex flex-wrap gap-1 cursor-pointer"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsEditing(true);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsEditing(true);
          }
        }}
      >
        {domainOptions.slice(0, 1).map((domain, index) => (
          <span
            key={index}
            className="inline-flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded hover:bg-gray-200 transition-colors"
            title={domain.value}
          >
            {domain.value.length > 10 ? `${domain.value.substring(0, 10)}...` : domain.value}
          </span>
        ))}
        {domainOptions.length >= 2 && (
          <span className="inline-flex items-center bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded hover:bg-gray-300 transition-colors">
            +{domainOptions.length - 1}
          </span>
        )}
      </div>
    );
  };

  if (isGrid) {
    return renderGridView();
  }

  return (
    <div className="w-full">
      <CreatableSelect
        placeholder={t("Add domains (e.g., example.com)")}
        isMulti
        styles={normalStyles}
        isClearable={false}
        value={domainOptions}
        options={domainOptions}
        onChange={handleChange}
        onCreateOption={handleCreate}
        formatCreateLabel={(inputValue) =>
          t('Add "{{domain}}"', { domain: formatDomain(inputValue) })
        }
        isValidNewOption={(inputValue) => {
          const formatted = formatDomain(inputValue);
          return formatted.length > 0 && validateDomain(formatted);
        }}
        className={isInline ? "w-full" : "text-sm"}
        classNamePrefix={isInline ? "select-inline" : undefined}
      />
      {!isInline && (
        <p className="mt-1 text-xs text-gray-500">
          {t("Enter domain names without https:// or www.")}
        </p>
      )}
    </div>
  );
};

export default DomainsField;
